import React, { useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from './TanTable';
import NotificationEmailModal from './NotificationEmailModal';
import { useHistoryPanelData } from '../hooks/useHistoryPanelData';
import { formatHistoryDateTime } from '../utils/dateUtils';

interface NotificationRecord {
  /** null for orphan junction rows (LEFT JOIN miss on notificationssent). */
  recno: number | null;
  datetime: string | null;
  createdby: string;
  type: string;
  emailfrom: string;
  emailto: string;
  subject: string;
}

interface NotificationsResponse {
  count: number;
  records: NotificationRecord[];
}

interface NotificationsPanelProps {
  pronumber: number | null;
  /** Defer fetching until the panel is actually shown. */
  active?: boolean;
  /** Bump this number to trigger a refetch from the parent. */
  refreshNonce?: number;
}

export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  pronumber,
  active = true,
  refreshNonce = 0,
}) => {
  const [emailRecno, setEmailRecno] = useState<number | null>(null);

  const { data, loading, error } = useHistoryPanelData<NotificationsResponse>(
    pronumber ? `/orders/${pronumber}/notifications` : null,
    active,
    refreshNonce,
    'Failed to fetch notifications'
  );

  const columns = useMemo<ColumnDef<NotificationRecord>[]>(
    () => [
      {
        accessorKey: 'datetime',
        header: 'Sent Date/Time',
        size: 160,
        cell: (info) => formatHistoryDateTime(info.getValue() as string | null) || '-',
      },
      {
        accessorKey: 'createdby',
        header: 'Created By',
        size: 110,
        cell: (info) => (info.getValue() as string) || '-',
      },
      {
        accessorKey: 'type',
        header: 'Notification Type',
        size: 150,
        cell: (info) => (info.getValue() as string) || '-',
      },
      {
        accessorKey: 'emailfrom',
        header: 'Email From',
        size: 160,
        cell: (info) => {
          const v = info.getValue() as string;
          if (!v) return '-';
          return (
            <div className="truncate" title={v}>
              {v}
            </div>
          );
        },
      },
      {
        accessorKey: 'emailto',
        header: 'Email To',
        size: 210,
        cell: (info) => {
          const v = info.getValue() as string;
          if (!v) return '-';
          return (
            <div className="truncate" title={v}>
              {v}
            </div>
          );
        },
      },
      {
        accessorKey: 'subject',
        header: 'Subject',
        size: 240,
        cell: (info) => {
          const record = info.row.original;
          const v = info.getValue() as string;
          // Orphan junction rows have no notificationssent record to open.
          if (record.recno == null) return v || '-';
          return (
            <button
              type="button"
              onClick={() => setEmailRecno(record.recno)}
              className="block w-full max-w-full text-left truncate text-blue-600 hover:underline dark:text-blue-400"
              title={v || 'View email'}
            >
              {v || '-'}
            </button>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="h-full flex flex-col">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-3">
          <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}
      <div className="flex-1 min-h-[200px]">
        <TanTable
          data={data?.records || []}
          columns={columns}
          loading={loading}
          cardTitle="Notifications"
          searchPlaceholder="Search notifications..."
          settingsKey="pro-history-notifications"
          fillContainer
          enableSearch
          enableReset
          enableShowHideCols
          enableColumnFilters
          enableZebraStriping={false}
          defaultSorting={[{ id: 'datetime', desc: true }]}
        />
      </div>
      <NotificationEmailModal recno={emailRecno} onClose={() => setEmailRecno(null)} />
    </div>
  );
};

export default NotificationsPanel;
